import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
  SceneCopy,
  SceneMedia,
  SceneRecipient,
  sceneMarkup,
} from './scene-markup';
import {
  clampStep,
  createSceneMotion,
  LOOSE_ROTATIONS,
  SceneMotion,
} from './scene-motion';
import './scenes.css';

type OnboardingScenesProps = {
  step: number;
  media: (SceneMedia | null)[];
  recipient: SceneRecipient;
  scenes: SceneCopy[];
  assetBase?: string;
  pageOffset?: number;
  pageCount?: number;
};

// Three persistent introduction scenes (ported from the prototype's
// embedded, fitted, cropped layout). One DOM is built per content; changing
// steps only drives the motion controller, never rebuilds the markup.
export const OnboardingScenes = ({
  step,
  media,
  recipient,
  scenes,
  assetBase = '/onboarding-scenes/assets',
  pageOffset = 1,
  pageCount = 4,
}: OnboardingScenesProps) => {
  const current = clampStep(step);
  const rootRef = useRef<HTMLDivElement>(null);
  const fitRef = useRef<HTMLDivElement>(null);
  const designRef = useRef<HTMLDivElement>(null);
  const controller = useRef<SceneMotion | null>(null);
  const cameraPaint = useRef<
    (pose: { position: number; assembly: number; time: number }) => void
  >(() => {});
  const cameraMeasure = useRef<() => void>(() => {});
  const motionPose = useRef({
    position: current,
    assembly: current ? 1 : 0,
    time: current === 2 ? 1 : 0,
  });
  const contentKey = JSON.stringify({ media, recipient, scenes });
  const markup = useMemo(
    () => ({ __html: sceneMarkup(assetBase, JSON.parse(contentKey)) }),
    [assetBase, contentKey],
  );
  const base = assetBase.replace(/\/$/, '');
  const hasMedia = media.some((item) => item && !item.sample);

  // Build only from the local snapshot; routing never replaces this DOM.
  useLayoutEffect(() => {
    if (!rootRef.current) return undefined;
    controller.current = createSceneMotion(rootRef.current, current, {
      onMeasure: () => cameraMeasure.current(),
      onPaint: (position, progress) => {
        motionPose.current = { position, ...progress };
        cameraPaint.current(motionPose.current);
      },
    });
    return () => {
      controller.current?.destroy();
      controller.current = null;
    };
  }, [markup]);

  // Fit one persistent desktop illustration into the remaining artwork area.
  // Its logical coordinates stay fixed while the surrounding viewport changes.
  useLayoutEffect(() => {
    const frame = fitRef.current;
    const design = designRef.current;
    const root = rootRef.current;
    if (!frame || !design || !root) return undefined;
    const stage = root.querySelector<HTMLElement>('.gs-scene-stage')!;
    const setProperty = (element: HTMLElement, name: string, value: string) => {
      if (element.style.getPropertyValue(name) !== value)
        element.style.setProperty(name, value);
    };
    let endpointScales: number[] | null = null;
    let viewportHeight = 0;
    const paintCamera = ({
      position,
      assembly,
      time,
    }: {
      position: number;
      assembly: number;
      time: number;
    }) => {
      if (!endpointScales) return;
      const from = position < 1 ? 0 : 1;
      const to = from + 1;
      const progress = position < 1 ? assembly : time;
      // Interpolate the logical viewport budget. This keeps the moving content
      // inside both endpoint budgets, including its captions, while zooming.
      const scale =
        1 /
        (1 / endpointScales[from] +
          (1 / endpointScales[to] - 1 / endpointScales[from]) * progress);
      const topGap = 28;
      const cropDepth = 24;
      setProperty(design, '--scene-fit-scale', String(scale));
      setProperty(design, '--scene-fit-top', `${topGap - 28 * scale}px`);
      setProperty(
        root,
        '--scene-frame-height',
        `${(viewportHeight - topGap + cropDepth) / scale}px`,
      );
    };
    const fit = () => {
      const width = frame.clientWidth;
      const height = frame.clientHeight;
      if (!width || !height || !design.offsetHeight) return;
      viewportHeight = height;
      // The hidden source layouts are stable across every transition. Measure
      // complete endpoint content once, regardless of the current fade/pose.
      const stageBox = stage.getBoundingClientRect();
      const ancestorScale = stageBox.width / stage.clientWidth;
      if (!ancestorScale) return;
      const contentSelector =
        '.vault-topbar,.vault-album-title,.vault-navigation,.vault-tile,.vault-category-sidebar,.vault-overview-context';
      const loose = [
        ...root.querySelectorAll<HTMLElement>('.gs-loose-item'),
      ].filter((node) => getComputedStyle(node).visibility !== 'hidden');
      const endpoints = [
        loose.map((node) => ({
          node: node.querySelector('.vault-media,.photo-slot'),
          rotation: LOOSE_ROTATIONS[node.dataset.loose ?? ''] || 0,
        })),
        ...[...root.querySelectorAll('[data-frame]')].map((anchor) =>
          [...anchor.querySelectorAll(contentSelector)].map((node) => ({
            node,
            rotation: 0,
          })),
        ),
      ];
      endpointScales = endpoints.map((nodes, index) => {
        let halfWidth = 0;
        let bottom = 28;
        nodes.forEach(({ node, rotation }) => {
          if (!node) return;
          const rect = node.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const x = (rect.left - stageBox.left) / ancestorScale;
          const y = (rect.top - stageBox.top) / ancestorScale;
          const w = rect.width / ancestorScale;
          const h = rect.height / ancestorScale;
          const radians = (rotation * Math.PI) / 180;
          const rotatedWidth =
            Math.abs(w * Math.cos(radians)) + Math.abs(h * Math.sin(radians));
          const rotatedHeight =
            Math.abs(h * Math.cos(radians)) + Math.abs(w * Math.sin(radians));
          halfWidth = Math.max(
            halfWidth,
            600 - (x + (w - rotatedWidth) / 2),
            x + (w + rotatedWidth) / 2 - 600,
          );
          bottom = Math.max(bottom, y + (h + rotatedHeight) / 2);
        });
        if (index) {
          const anchor = root.querySelector(
            index === 1
              ? '[data-frame="album"] .vault-frame'
              : '[data-frame="timeline"] .vault-frame',
          );
          if (anchor)
            halfWidth = Math.max(
              halfWidth,
              anchor.getBoundingClientRect().width / ancestorScale / 2,
            );
        }
        const topGap = 28;
        const bottomGap = 32;
        const shadowGutter = index ? 48 : 24;
        return Math.max(
          0.01,
          Math.min(
            1.08,
            (width - 32) / (2 * halfWidth + 2 * shadowGutter),
            (height - topGap - bottomGap) / (bottom - 28),
          ),
        );
      });
      paintCamera(motionPose.current);
    };
    cameraPaint.current = paintCamera;
    cameraMeasure.current = fit;
    fit();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    observer?.observe(frame);
    window.addEventListener('resize', fit);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', fit);
      cameraPaint.current = () => {};
      cameraMeasure.current = () => {};
    };
  }, [markup]);

  useEffect(() => {
    controller.current?.setStep(current);
  }, [current]);

  return (
    <div ref={fitRef} className="gs-scene-fit is-cropped">
      <div ref={designRef} className="gs-scene-design">
        <div className="gs-scenes-container">
          <div
            ref={rootRef}
            className="gs-onboarding-scenes is-embedded is-fitted is-cropped"
            onErrorCapture={(event) => {
              const target = event.target as HTMLElement;
              if (target.tagName === 'IMG') target.hidden = true;
            }}
          >
            <style>{`@font-face{font-family:GSScenesSpectral;font-style:normal;font-weight:400;font-display:swap;src:url("${base}/fonts/spectral-latin-400-normal.woff2") format("woff2")}@font-face{font-family:GSScenesSpectral;font-style:normal;font-weight:500;font-display:swap;src:url("${base}/fonts/spectral-latin-500-normal.woff2") format("woff2")}`}</style>
            {/* eslint-disable-next-line react/no-danger */}
            <div className="gs-scene-stage" dangerouslySetInnerHTML={markup} />
            <span className="gs-scenes-sr-only" aria-live="off">
              Page {current + 1 + pageOffset} of {pageCount}.{' '}
              {scenes[current].title}
            </span>
            <span className="gs-scenes-sr-only">
              {hasMedia
                ? 'Some of your own pictures appear with illustrated examples of photos, letters, documents, and audio.'
                : 'Illustrated examples of videos, photos, letters, documents, and audio.'}{' '}
              Blank titles and dates represent possible future content. This is
              a preview; its play icons are part of the illustration.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
